import React, {useState} from 'react';
import {Dimensions, ScrollView} from 'react-native';
import Feather from 'react-native-vector-icons/Feather';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import Modal from 'react-native-modal';
import {SafeAreaView} from 'react-native-safe-area-context';

const SHEET_MAX_HEIGHT = Dimensions.get('window').height * 0.65;
import {useRoute} from '@react-navigation/native';
import {useTheme} from 'styled-components/native';
import {format, parseISO} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import {RFValue} from 'react-native-responsive-fontsize';

import {Header} from '../../../../components/Interface/Headers/Header';
import {Button} from '../../../../components/Interface/Forms/Buttons/Button';
import {useGlobal} from '../../../../context/global';
import {Maintenance} from '../../../../components/Interface/Maintenance';
import {PPTrackRequestResultDTO} from '../../../../dtos/ProntoPagueiDTO';

import {
  Container,
  ScrollWrapper,
  Content,
  PageTitle,
  PageSubtitle,
  StatusCard,
  StatusTopRow,
  StatusIconCircle,
  StatusTextGroup,
  StatusLabel,
  StatusValue,
  StatusSeparator,
  TotalLabel,
  TotalValue,
  LiquidationRow,
  LiquidationText,
  SectionTitle,
  InfoCard,
  InfoRow,
  InfoRowWithIcon,
  InfoIconWrapper,
  InfoRowContent,
  InfoSeparator,
  InfoLabel,
  InfoValue,
  FinancialRow,
  FinancialLabel,
  FinancialValue,
  FinancialLabelBold,
  TotalBadge,
  TotalBadgeText,
  VerDebitsRow,
  VerDebitsText,
  SheetContainer,
  SheetHandle,
  SheetTitle,
  SheetCard,
  Footer,
} from './styles';

function formatCurrency(value: string): string {
  const num = parseFloat(value.replace(/\./g, '').replace(',', '.'));
  return num.toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'});
}

function formatDate(date?: string): string {
  if (!date) return '';
  try {
    return format(parseISO(date), 'dd/MM/yyyy', {locale: ptBR});
  } catch {
    try {
      return format(new Date(date), 'dd/MM/yyyy', {locale: ptBR});
    } catch {
      return date;
    }
  }
}

function formatDateTime(date?: string): string {
  if (!date) return '';
  try {
    return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", {locale: ptBR});
  } catch {
    return date;
  }
}

function getStatusConfig(status: string | undefined, colors: any) {
  const lower = (status ?? '').toLowerCase();
  if (lower.includes('liquid') || lower.includes('pago')) {
    return {
      label: 'Liquidado',
      cardBorder: colors.success,
      cardBg: colors.success_bg,
      iconBg: colors.success,
      iconWrapperBg: colors.success_bg,
      textColor: colors.success,
      icon: 'check' as const,
    };
  }
  if (
    lower.includes('recus') ||
    lower.includes('cancel') ||
    lower.includes('reprov')
  ) {
    return {
      label: 'Recusado',
      cardBorder: colors.red,
      cardBg: '#FFEBEE',
      iconBg: colors.red,
      iconWrapperBg: '#FFEBEE',
      textColor: colors.red,
      icon: 'x' as const,
    };
  }
  return {
    label: 'Em análise',
    cardBorder: '#ffd064',
    cardBg: colors.gold_opacity,
    iconBg: '#ffd064',
    iconWrapperBg: colors.gold_opacity,
    textColor: '#B8860B',
    icon: 'clock' as const,
  };
}

export function PP_TrackRequestDetail() {
  const theme = useTheme();
  const {params} = useRoute<any>();
  const item: PPTrackRequestResultDTO = params.item;

  const [showDebitosSheet, setShowDebitosSheet] = useState(false);

  const {disabledFeatures} = useGlobal();
  const isDisabled = (key: string) => disabledFeatures.includes(key);

  const {label, cardBorder, cardBg, iconBg, iconWrapperBg, textColor, icon} = getStatusConfig(
    item.status,
    theme.colors,
  );

  const liquidacaoData = item.transacao.debitos.find(
    d => d.liquidacao_data,
  )?.liquidacao_data;

  const beneficiario = item.transacao.debitos.find(
    d => d.liquidacao_beneficiario,
  )?.liquidacao_beneficiario;

  if (isDisabled('multas')) {
    return <Maintenance />;
  }

  return (
    <>
      <Container>
        <Header icon="chevron-left" />
        <ScrollWrapper contentContainerStyle={{paddingBottom: RFValue(20)}}>
          <Content>
            <PageTitle>Acompanhar solicitações</PageTitle>
            {!!item.protocolo && (
              <PageSubtitle>Protocolo: {item.protocolo}</PageSubtitle>
            )}

            <StatusCard
              style={{borderColor: cardBorder, backgroundColor: cardBg}}>
              <StatusTopRow>
                <StatusIconCircle style={{backgroundColor: iconBg}}>
                  <Feather name={icon} size={RFValue(20)} color="#FFFFFF" />
                </StatusIconCircle>
                <StatusTextGroup>
                  <StatusLabel>Situação</StatusLabel>
                  <StatusValue style={{color: textColor}}>{label}</StatusValue>
                </StatusTextGroup>
              </StatusTopRow>

              <StatusSeparator />

              <TotalLabel>Total pago</TotalLabel>
              <TotalValue>
                {formatCurrency(item.transacao.Values.TotalPaymentValue)}
              </TotalValue>

              {!!liquidacaoData && (
                <LiquidationRow>
                  <Feather
                    name="clock"
                    size={RFValue(11)}
                    color={theme.colors.text}
                  />
                  <LiquidationText>
                    Liquidado em {formatDateTime(liquidacaoData)}
                  </LiquidationText>
                </LiquidationRow>
              )}
            </StatusCard>

            <SectionTitle>Resumo financeiro</SectionTitle>
            <InfoCard accentColor={cardBorder}>
              <FinancialRow>
                <FinancialLabel>Valor dos débitos</FinancialLabel>
                <FinancialValue>
                  {formatCurrency(item.transacao.Values.DebitsSumValue)}
                </FinancialValue>
              </FinancialRow>
              <InfoSeparator />
              <FinancialRow>
                <FinancialLabel>
                  Parcelamento{item.parcelas ? ` ${item.parcelas}x` : ''}
                </FinancialLabel>
                <FinancialValue>
                  {formatCurrency(item.transacao.Values.InstallmentValue)}
                </FinancialValue>
              </FinancialRow>
              <InfoSeparator />
              <FinancialRow>
                <FinancialLabelBold>Total pago</FinancialLabelBold>
                <TotalBadge bg={cardBg}>
                  <TotalBadgeText>
                    {formatCurrency(item.transacao.Values.TotalPaymentValue)}
                  </TotalBadgeText>
                </TotalBadge>
              </FinancialRow>
            </InfoCard>

            {item.transacao.debitos.length > 0 && (
              <>
                <SectionTitle>Motivo</SectionTitle>
                {item.transacao.debitos.length === 1 ? (
                  <InfoCard accentColor={cardBorder}>
                    <InfoRowWithIcon>
                      <InfoIconWrapper>
                        <MaterialCommunityIcons
                          name="tag-outline"
                          size={RFValue(16)}
                          color={theme.colors.text}
                        />
                      </InfoIconWrapper>
                      <InfoRowContent>
                        <InfoLabel>Tipo</InfoLabel>
                        <InfoValue>{item.transacao.debitos[0].tipo}</InfoValue>
                      </InfoRowContent>
                    </InfoRowWithIcon>
                    {!!item.transacao.debitos[0].descricao && (
                      <>
                        <InfoSeparator />
                        <InfoRowWithIcon>
                          <InfoIconWrapper>
                            <MaterialCommunityIcons
                              name="text-box-outline"
                              size={RFValue(16)}
                              color={theme.colors.text}
                            />
                          </InfoIconWrapper>
                          <InfoRowContent>
                            <InfoLabel>Descrição</InfoLabel>
                            <InfoValue>{item.transacao.debitos[0].descricao}</InfoValue>
                          </InfoRowContent>
                        </InfoRowWithIcon>
                      </>
                    )}
                  </InfoCard>
                ) : (
                  <InfoCard accentColor={cardBorder}>
                    <VerDebitsRow onPress={() => setShowDebitosSheet(true)}>
                      <VerDebitsText>Ver todos os tipos de débitos</VerDebitsText>
                      <Feather name="chevron-right" size={RFValue(16)} color={theme.colors.blue} />
                    </VerDebitsRow>
                  </InfoCard>
                )}
              </>
            )}

            <Modal
              isVisible={showDebitosSheet}
              onBackdropPress={() => setShowDebitosSheet(false)}
              onSwipeComplete={() => setShowDebitosSheet(false)}
              swipeDirection="down"
              style={{justifyContent: 'flex-end', margin: 0}}>
              <SheetContainer>
                <SheetHandle />
                <SheetTitle>Tipos de débitos</SheetTitle>
                <ScrollView
                  style={{maxHeight: SHEET_MAX_HEIGHT}}
                  contentContainerStyle={{paddingBottom: RFValue(8)}}
                  showsVerticalScrollIndicator={false}
                  bounces={false}>
                {item.transacao.debitos.map((debito, index) => (
                  <SheetCard key={index}>
                    <InfoRowWithIcon>
                      <InfoIconWrapper>
                        <MaterialCommunityIcons
                          name="tag-outline"
                          size={RFValue(16)}
                          color={theme.colors.text}
                        />
                      </InfoIconWrapper>
                      <InfoRowContent>
                        <InfoLabel>Tipo</InfoLabel>
                        <InfoValue>{debito.tipo}</InfoValue>
                      </InfoRowContent>
                    </InfoRowWithIcon>
                    {!!debito.descricao && (
                      <>
                        <InfoSeparator />
                        <InfoRowWithIcon>
                          <InfoIconWrapper>
                            <MaterialCommunityIcons
                              name="text-box-outline"
                              size={RFValue(16)}
                              color={theme.colors.text}
                            />
                          </InfoIconWrapper>
                          <InfoRowContent>
                            <InfoLabel>Descrição</InfoLabel>
                            <InfoValue>{debito.descricao}</InfoValue>
                          </InfoRowContent>
                        </InfoRowWithIcon>
                      </>
                    )}
                  </SheetCard>
                ))}
                </ScrollView>
              </SheetContainer>
            </Modal>

            <SectionTitle>Dados da solicitação</SectionTitle>
            <InfoCard accentColor={cardBorder}>
              <InfoRowWithIcon>
                <InfoIconWrapper >
                  <MaterialCommunityIcons
                    name="card-account-details-outline"
                    size={RFValue(16)}
                    color={theme.colors.text}
                  />
                </InfoIconWrapper>
                <InfoRowContent>
                  <InfoLabel>Placa</InfoLabel>
                  <InfoValue>{item.transacao.placa}</InfoValue>
                </InfoRowContent>
              </InfoRowWithIcon>
              <InfoSeparator />
              <InfoRowWithIcon>
                <InfoIconWrapper >
                  <MaterialCommunityIcons
                    name="office-building-outline"
                    size={RFValue(16)}
                    color={theme.colors.text}
                  />
                </InfoIconWrapper>
                <InfoRowContent>
                  <InfoLabel>Convênio</InfoLabel>
                  <InfoValue>{item.transacao.convenio.nome}</InfoValue>
                </InfoRowContent>
              </InfoRowWithIcon>
              {!!beneficiario && (
                <>
                  <InfoSeparator />
                  <InfoRowWithIcon>
                    <InfoIconWrapper >
                      <MaterialCommunityIcons
                        name="account-outline"
                        size={RFValue(16)}
                        color={theme.colors.text}
                      />
                    </InfoIconWrapper>
                    <InfoRowContent>
                      <InfoLabel>Beneficiário</InfoLabel>
                      <InfoValue>{beneficiario}</InfoValue>
                    </InfoRowContent>
                  </InfoRowWithIcon>
                </>
              )}
              {!!item.data_compra && (
                <>
                  <InfoSeparator />
                  <InfoRowWithIcon>
                    <InfoIconWrapper >
                      <MaterialCommunityIcons
                        name="calendar-outline"
                        size={RFValue(16)}
                        color={theme.colors.text}
                      />
                    </InfoIconWrapper>
                    <InfoRowContent>
                      <InfoLabel>Data da solicitação</InfoLabel>
                      <InfoValue>{formatDate(item.data_compra)}</InfoValue>
                    </InfoRowContent>
                  </InfoRowWithIcon>
                </>
              )}
            </InfoCard>
          </Content>
        </ScrollWrapper>
      </Container>
      <Footer>
        <SafeAreaView edges={['bottom']}>
          <Button title="Atualizar" enabled onPress={() => {}} />
        </SafeAreaView>
      </Footer>
    </>
  );
}
